import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import FileUpload from "../helper/FileUpload";
import { fetchData } from "../helper/api.interceptor";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

const EditColorVariant = () => {
  const navigate = useNavigate();
  const { productId } = useParams();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [variants, setVariants] = useState([]);

  // For no-variant product images upload (multiple)
  const [mainImageFiles, setMainImageFiles] = useState([]);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);

  /* ========================== FETCH PRODUCT ========================== */
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetchData.get(`product/details/${productId}`);
        if (!res?.result) {
          Swal.fire("Error", "Product not found", "error");
          navigate("/product_details");
          return;
        }

        // Store existing product_main_images if any
        if (res.result.product_main_images && res.result.product_main_images.length > 0) {
          const formattedMain = res.result.product_main_images.map(url => ({
            preview: url,
            isExisting: true,
            originalUrl: url,
          }));
          setMainImageFiles(formattedMain);
        }

        const formatImages = (urls = []) =>
          urls.map((url) => ({
            preview: url,
            isExisting: true,
            originalUrl: url,
          }));

        const formatted = res.result.color_variants.map((v) => ({
          ...v,
          product_images: formatImages(v.product_images),
          main_images: formatImages(v.main_images),
          color_variant_listing_image: v.color_variant_listing_image
            ? formatImages([v.color_variant_listing_image])
            : [],
          place_order_images: formatImages(v.place_order_images),
        }));

        setVariants(formatted);
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to load product", "error");
      } finally {
        setFetching(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  if (fetching) return <p className="p-4">Loading product...</p>;

  /* ============================== SKIP (no API call) ============================== */
  const handleSkip = () => {
    navigate(`/product/edit-specification/${productId}`);
  };

  /* ========================= ADD ANOTHER BLANK VARIANT ROW ========================= */
  const addNewVariantRow = () => {
    setVariants((prev) => [
      ...prev,
      {
        color_name: "",
        is_default: false,
        product_images: [],
        main_images: [],
        color_variant_listing_image: [],
        place_order_images: [],
        isNew: true,
      },
    ]);
  };

  /* ======================= HANDLE IMAGE CHANGES ======================= */
  const handleImagesChange = (variantIndex, key, updatedImageList) => {
    setVariants((prev) => {
      const updated = [...prev];
      updated[variantIndex][key] = updatedImageList;
      return updated;
    });
  };

  /* ======================= APPEND TO FORM DATA ======================= */
  // Existing images remain unchanged unless the user chooses replacement files.
  // Sending only File objects prevents the edit screen from re-uploading every
  // existing image whenever a variant is saved.
  const appendImages = (formData, variant, fieldKey, apiKey) => {
    (variant[fieldKey] || []).forEach((image) => {
      if (image.file instanceof File) {
        formData.append(apiKey, image.file);
      }
    });
  };

  const getExistingImageUrls = (images = []) =>
    images
      .filter((image) => image.isExisting && image.originalUrl)
      .map((image) => image.originalUrl);

  /* =================== UPLOAD PRODUCT MAIN IMAGES (no-variant) =================== */
  const handleUploadMainImages = async () => {
    if (!mainImageFiles || mainImageFiles.length === 0) {
      Swal.fire("Warning", "Please select at least one image", "warning");
      return;
    }

    setUploadingMainImage(true);
    try {
      const formData = new FormData();

      // Separate existing files (string URLs) and new files (File objects)
      const existingUrls = [];
      
      for (const img of mainImageFiles) {
        if (img.file instanceof File) {
          // Newly selected file
          formData.append("product_main_images", img.file);
        } else if (img.isExisting && img.preview) {
          // Existing image URL
          existingUrls.push(img.preview);
        }
      }

      formData.append("existing_main_images", JSON.stringify(existingUrls));

      const result = await fetchData.postWithFormData(
        `product/upload-main-image/${productId}`,
        formData
      );

      if (result?.status_code === 200) {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: "Product images saved successfully",
        }).then(() => {
          navigate(`/product/edit-specification/${productId}`);
        });
      } else {
        Swal.fire("Error", result?.message || "Failed to upload images", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setUploadingMainImage(false);
    }
  };

  /* ============================ SUBMIT (variants) ============================ */
  const handleUpdateAllVariants = async () => {
    setLoading(true);
    try {
      for (const variant of variants) {
        const formData = new FormData();
        formData.append("color_name", variant.color_name);
        formData.append("is_default", variant.is_default);

        await appendImages(formData, variant, "product_images", "productImages");
        await appendImages(formData, variant, "main_images", "mainImages");
        await appendImages(formData, variant, "color_variant_listing_image", "color_variant_listing_image");
        await appendImages(formData, variant, "place_order_images", "place_order_images");

        // Keep current files by reference. Only newly selected files are uploaded.
        formData.append(
          "existing_product_images",
          JSON.stringify(getExistingImageUrls(variant.product_images))
        );
        formData.append(
          "existing_main_images",
          JSON.stringify(getExistingImageUrls(variant.main_images))
        );
        formData.append(
          "existing_place_order_images",
          JSON.stringify(getExistingImageUrls(variant.place_order_images))
        );
        formData.append(
          "existing_color_variant_listing_image",
          getExistingImageUrls(variant.color_variant_listing_image)[0] || ""
        );

        if (variant.isNew) {
          await fetchData.postWithFormData(
            `product/add-color-variant/${productId}`,
            formData
          );
        } else {
          await fetchData.postWithFormData(
            `product/update-color-variant/${productId}/${variant.variant_id}`,
            formData
          );
        }
      }

      Swal.fire("Success", "All variants updated!", "success").then(() => {
        navigate(`/product/edit-specification/${productId}`);
      });
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Update failed", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ============================== UI ============================== */

  /* ─── NO COLOR VARIANTS: Show product images upload ─── */
  if (variants.length === 0) {
    return (
      <div>
        <div className="card mb-4">
          <div className="card-header bg-white border-bottom">
            <h6 className="fw-semibold mb-0">
              Product Images
              <small className="text-muted fw-normal ms-2">
                (No color variants — upload the main product images for the slider)
              </small>
            </h6>
          </div>
          <div className="card-body">
            <p className="text-muted mb-3">
              This product has no color variants. Please upload main product
              images so they appear in the slider correctly on the website.
            </p>

            <FileUpload
              label="Product Main Images"
              multiple={true}
              images={mainImageFiles}
              onImagesChange={setMainImageFiles}
            />

            <div className="d-flex gap-2 flex-wrap mt-4">
              {/* Save the uploaded images and go to next step */}
              <button
                className="btn btn-success"
                onClick={handleUploadMainImages}
                disabled={uploadingMainImage}
              >
                {uploadingMainImage ? "Saving..." : "Save & Continue to Specifications"}
              </button>

              {/* Add color variant instead */}
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/product/add-color-variant/${productId}`)}
                disabled={uploadingMainImage}
              >
                + Add Color Variant Instead
              </button>

              {/* Skip everything */}
              <button
                className="btn btn-secondary"
                onClick={handleSkip}
                disabled={uploadingMainImage}
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ─── HAS COLOR VARIANTS: Normal variant editing ─── */
  return (
    <div>
      {variants.map((variant, variantIndex) => (
        <div className="card mb-4" key={variant.variant_id || variantIndex}>
          <div className="card-body">
            {/* Color Name & Default Checkbox */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Color Name</label>
              <input
                className="form-control"
                value={variant.color_name}
                onChange={(e) => {
                  setVariants((prev) => {
                    const updated = [...prev];
                    updated[variantIndex].color_name = e.target.value;
                    return updated;
                  });
                }}
              />
            </div>

            <FormControlLabel
              control={
                <Checkbox
                  checked={variant.is_default}
                  onChange={(e) => {
                    setVariants((prev) =>
                      prev.map((v, i) => ({
                        ...v,
                        is_default: i === variantIndex ? e.target.checked : false,
                      }))
                    );
                  }}
                />
              }
              label="Set as Default Color"
            />

            {/* FILE UPLOADS */}
            <FileUpload
              label="Product Images"
              multiple
              images={variant.product_images}
              onImagesChange={(updatedList) =>
                handleImagesChange(variantIndex, "product_images", updatedList)
              }
            />

            <FileUpload
              label="Main Images"
              multiple
              images={variant.main_images}
              onImagesChange={(updatedList) =>
                handleImagesChange(variantIndex, "main_images", updatedList)
              }
            />

            <FileUpload
              label="Color Variant Listing Image"
              multiple={false}
              maxFiles={1}
              images={variant.color_variant_listing_image}
              onImagesChange={(updatedList) =>
                handleImagesChange(variantIndex, "color_variant_listing_image", updatedList)
              }
            />

            <FileUpload
              label="Place Order Images"
              multiple={false}
              maxFiles={1}
              images={variant.place_order_images}
              onImagesChange={(updatedList) =>
                handleImagesChange(variantIndex, "place_order_images", updatedList)
              }
            />
          </div>
        </div>
      ))}

      <div className="d-flex gap-2 flex-wrap mt-3">
        <button
          className="btn btn-primary"
          onClick={addNewVariantRow}
          disabled={loading}
        >
          Add Other
        </button>

        <button
          className="btn bg-custom px-16"
          onClick={handleUpdateAllVariants}
          disabled={loading}
        >
          {loading ? "Updating..." : "Update All Variants"}
        </button>

        <button
          className="btn btn-secondary"
          onClick={handleSkip}
          disabled={loading}
        >
          Skip
        </button>
      </div>
    </div>
  );
};

export default EditColorVariant;
